/**
 * Sistema di Persistenza Sessione FantAPP v2
 *
 * Persistenza dello stato UI:
 * - DEV (`import.meta.env.DEV`): localStorage → resta loggati tra refresh e riaperture tab
 * - PROD: sessionStorage → persiste al refresh, cancellato alla chiusura tab/browser
 * - Prefisso versionato per migrazione sicura
 */

import { sanitizePlayerListNames } from '../utils/decodeHtmlEntities.js';

const KEY_PREFIX = 'fantapp:v2:';
const KEYS = {
  session: KEY_PREFIX + 'session',
  view: KEY_PREFIX + 'ui:view',
  auctionMode: KEY_PREFIX + 'auctionMode',
  activeAuctionId: KEY_PREFIX + 'activeAuctionId',
  players: KEY_PREFIX + 'players',
  playersMeta: KEY_PREFIX + 'players:meta',
  version: KEY_PREFIX + 'version',
};

const AUCTION_MODES = new Set(['prep', 'live']);

/** In test/dev evita di rifare login a ogni chiusura tab; in prod resta session-bound. */
const USE_DURABLE_STORAGE = Boolean(import.meta.env?.DEV);

function getStore() {
  return USE_DURABLE_STORAGE ? localStorage : sessionStorage;
}

/**
 * Parse JSON sicuro con fallback
 */
function safeParse(str, fallback = null) {
  if (!str) return fallback;
  try {
    return JSON.parse(str);
  } catch (error) {
    console.warn('[SessionPersistence] Parse error:', error.message);
    return fallback;
  }
}

/**
 * Set item sicuro con JSON stringify
 */
function setItem(key, value) {
  try {
    getStore().setItem(key, JSON.stringify(value));
    return true;
  } catch (error) {
    console.error('[SessionPersistence] Save error:', error.message);
    return false;
  }
}

/**
 * Get item sicuro con parse e fallback
 */
function getItem(key, fallback = null) {
  try {
    const store = getStore();
    let value = store.getItem(key);

    // Migrazione one-shot: sessione lasciata in sessionStorage da un run precedente
    if (value === null && USE_DURABLE_STORAGE) {
      value = sessionStorage.getItem(key);
      if (value !== null) {
        try {
          localStorage.setItem(key, value);
          sessionStorage.removeItem(key);
        } catch (_) { /* ignore migration errors */ }
      }
    }

    return safeParse(value, fallback);
  } catch (error) {
    console.error('[SessionPersistence] Load error:', error.message);
    return fallback;
  }
}

/**
 * Check dimensione storage sicuro
 */
function checkStorageSize(data) {
  const json = JSON.stringify(data);
  const sizeKB = Math.round(json.length / 1024);
  const maxKB = 4500; // ~4.5MB (lascia margine per i 5MB tipici)
  
  if (sizeKB > maxKB) {
    console.warn(`[SessionPersistence] Data too large: ${sizeKB}KB > ${maxKB}KB, skipping persist`);
    return false;
  }
  return true;
}

/**
 * API Pubblica per Persistenza Sessione
 */
export const SessionPersistence = {
  
  // ===== SESSIONE UTENTE =====
  
  /**
   * Verifica se esiste una sessione attiva
   */
  hasActiveSession() {
    const session = getItem(KEYS.session, null);
    return !!(session && session.access_key);
  },

  /**
   * Ottieni dati sessione corrente
   */
  getSession() {
    return getItem(KEYS.session, null);
  },

  /**
   * Salva sessione utente
   */
  saveSession(sessionData) {
    if (!sessionData || !sessionData.access_key) {
      console.warn('[SessionPersistence] Invalid session data, not saving');
      return false;
    }

    const success = setItem(KEYS.session, {
      access_key: sessionData.access_key,
      user_id: sessionData.user_id || sessionData.id,
      display_name: sessionData.display_name,
      created_at: sessionData.created_at || new Date().toISOString(),
      last_activity: new Date().toISOString()
    });

    if (success) {
      setItem(KEYS.version, '2');
      console.log('[SessionPersistence] Session saved for:', sessionData.display_name || sessionData.access_key);
    }

    return success;
  },

  /**
   * Cancella sessione e tutti i dati correlati
   */
  clearSession() {
    try {
      // Rimuovi da entrambi gli store (evita residui dopo switch DEV/PROD)
      Object.values(KEYS).forEach(key => {
        sessionStorage.removeItem(key);
        localStorage.removeItem(key);
      });

      console.log('[SessionPersistence] Session cleared');
      return true;
    } catch (error) {
      console.error('[SessionPersistence] Clear session error:', error);
      return false;
    }
  },

  // ===== VISTA UI =====

  /**
   * Imposta vista corrente con sub-vista opzionale
   */
  setView(viewName, subView = null) {
    if (!viewName) return false;

    const viewData = {
      name: viewName,
      subView: subView,
      timestamp: Date.now()
    };

    const success = setItem(KEYS.view, viewData);
    if (success && window.DEBUG_MODE === 'true') {
      console.log(`[SessionPersistence] View set: ${viewName}${subView ? ` → ${subView}` : ''}`);
    }
    return success;
  },

  /**
   * Ottieni vista corrente
   */
  getView() {
    return getItem(KEYS.view, null);
  },

  // ===== MODALITÀ ASTA (prep / live) =====

  /**
   * Modalità asta corrente: 'prep' (default) o 'live'
   */
  getAuctionMode() {
    const mode = getItem(KEYS.auctionMode, 'prep');
    return mode === 'live' ? 'live' : 'prep';
  },

  /**
   * Persiste modalità asta
   * @param {'prep'|'live'} mode
   */
  setAuctionMode(mode) {
    if (!AUCTION_MODES.has(mode)) {
      console.warn('[SessionPersistence] Invalid auction mode:', mode);
      return false;
    }
    return setItem(KEYS.auctionMode, mode);
  },

  // ===== ASTA ATTIVA (file asta) =====

  getActiveAuctionId() {
    const id = getItem(KEYS.activeAuctionId, null);
    return typeof id === 'string' && id ? id : null;
  },

  setActiveAuctionId(auctionId) {
    if (!auctionId || typeof auctionId !== 'string') {
      try {
        getStore().removeItem(KEYS.activeAuctionId);
        if (USE_DURABLE_STORAGE) sessionStorage.removeItem(KEYS.activeAuctionId);
      } catch (_) { /* ignore */ }
      return true;
    }
    return setItem(KEYS.activeAuctionId, auctionId);
  },

  // ===== GIOCATORI =====

  /**
   * Salva array giocatori con controllo dimensioni
   */
  savePlayers(players) {
    if (!Array.isArray(players)) {
      console.warn('[SessionPersistence] Players must be an array, not saving');
      return false;
    }

    const clean = sanitizePlayerListNames(players);

    if (!checkStorageSize(clean)) {
      // Fallback: salva solo i metadati
      setItem(KEYS.playersMeta, {
        count: clean.length,
        updatedAt: Date.now(),
        tooLarge: true
      });
      return false;
    }

    const success = setItem(KEYS.players, clean);
    if (success) {
      setItem(KEYS.playersMeta, {
        count: clean.length,
        updatedAt: Date.now(),
        tooLarge: false
      });
      
      if (window.DEBUG_MODE === 'true') {
        console.log(`[SessionPersistence] Players saved: ${clean.length} items`);
      }
    }

    return success;
  },

  /**
   * Carica array giocatori
   */
  loadPlayers() {
    const players = getItem(KEYS.players, []);
    const list = Array.isArray(players) ? sanitizePlayerListNames(players) : [];
    if (window.DEBUG_MODE === 'true') {
      console.log(`[SessionPersistence] Players loaded: ${list.length} items`);
    }
    return list;
  },

  /**
   * Verifica se ci sono giocatori salvati
   */
  hasPlayers() {
    const meta = getItem(KEYS.playersMeta, null);
    if (meta && meta.count > 0) {
      return true;
    }
    
    // Fallback: controlla array diretto
    const players = getItem(KEYS.players, []);
    return Array.isArray(players) && players.length > 0;
  },

  /**
   * Ottieni metadati giocatori senza caricare l'array completo
   */
  getPlayersMeta() {
    return getItem(KEYS.playersMeta, { count: 0, updatedAt: 0, tooLarge: false });
  },

  // ===== UTILITY E DEBUG =====

  /**
   * Pulisci dati obsoleti/corrotti
   */
  cleanupCorruptedData() {
    let cleaned = 0;
    const store = getStore();

    Object.entries(KEYS).forEach(([name, key]) => {
      try {
        const value = store.getItem(key);
        if (value !== null) {
          JSON.parse(value); // Test parse
        }
      } catch (error) {
        console.warn(`[SessionPersistence] Removing corrupted ${name}:`, error.message);
        store.removeItem(key);
        cleaned++;
      }
    });

    return cleaned;
  },

  /**
   * Stato completo per debug
   */
  getDebugState() {
    return {
      hasSession: this.hasActiveSession(),
      session: this.getSession(),
      view: this.getView(),
      auctionMode: this.getAuctionMode(),
      activeAuctionId: this.getActiveAuctionId(),
      playersMeta: this.getPlayersMeta(),
      version: getItem(KEYS.version, null),
      storageUsed: this._getStorageUsage()
    };
  },

  /**
   * Calcola utilizzo storage (privato)
   */
  _getStorageUsage() {
    let totalSize = 0;
    const usage = {};
    const store = getStore();

    Object.entries(KEYS).forEach(([name, key]) => {
      const value = store.getItem(key);
      const size = value ? value.length : 0;
      usage[name] = `${Math.round(size / 1024)}KB`;
      totalSize += size;
    });

    return {
      ...usage,
      total: `${Math.round(totalSize / 1024)}KB`,
      backend: USE_DURABLE_STORAGE ? 'localStorage' : 'sessionStorage',
    };
  }
};

// ===== COMPATIBILITÀ LEGACY =====

/**
 * Classe UserSession compatibile per non rompere import esistenti
 * Wrappa SessionPersistence mantenendo interfaccia originale
 */
export class UserSession {
  constructor() {
    this.currentUser = null;
    this.profile = null;
    this.session = null;
    
    // Ripristino stato se disponibile
    this._restoreState();
    
    console.log('[UserSession] Initialized with SessionPersistence v2');
  }

  _restoreState() {
    if (SessionPersistence.hasActiveSession()) {
      const session = SessionPersistence.getSession();
      this.currentUser = session;
      this.profile = {
        display_name: session.display_name,
        access_code: session.access_key
      };
      this.session = session;
    }
  }

  async login(accessCode) {
    try {
      console.log('[UserSession] Login attempt:', accessCode);
      
      // Validazione input
      if (!accessCode || accessCode.length < 3 || accessCode.length > 50) {
        return { success: false, error: 'Codice di accesso deve essere tra 3 e 50 caratteri' };
      }

      let isValid = false;
      let displayName = `User ${accessCode}`;

      try {
        const { validateAccessCode } = await import('./db/auth.pocketbase.js');
        const r = await validateAccessCode(accessCode);
        if (r.valid) {
          isValid = true;
          displayName = r.displayName;
          console.log('[UserSession] Validazione PocketBase OK:', accessCode);
        }
      } catch (dbError) {
        console.warn('[UserSession] PocketBase non disponibile, uso fallback:', dbError.message);
      }

      // Fallback: accetta codici di test se validazione database fallisce
      if (!isValid) {
        const testCodes = ['TEST123', 'ADMIN', 'INTER', 'MILAN', 'JUVE', 'DEMO', 'MANTRA_25'];
        if (testCodes.includes(accessCode.toUpperCase())) {
          isValid = true;
          displayName = `Test User ${accessCode}`;
          console.log('[UserSession] ✅ Validazione fallback riuscita per:', accessCode);
        }
      }

      if (!isValid) {
        return { success: false, error: 'Codice di accesso non valido' };
      }

      // Crea sessione
      const sessionData = {
        id: `session_${Date.now()}`,
        access_key: accessCode,
        display_name: displayName,
        created_at: new Date().toISOString()
      };

      // Salva in persistenza
      SessionPersistence.saveSession(sessionData);
      
      // Aggiorna stato interno
      this.currentUser = sessionData;
      this.profile = {
        display_name: sessionData.display_name,
        access_code: sessionData.access_key
      };
      this.session = sessionData;

      console.log('[UserSession] Login success:', sessionData.display_name);
      return { success: true, user: sessionData };

    } catch (error) {
      console.error('[UserSession] Login error:', error);
      return { success: false, error: 'Errore interno durante il login' };
    }
  }

  async restoreSessionFromCode(accessCode) {
    // Per semplicità, usa login
    const result = await this.login(accessCode);
    return result.success;
  }

  isAuthenticated() {
    return SessionPersistence.hasActiveSession() && !!(this.currentUser || this.session);
  }

  logout() {
    console.log('[UserSession] Logout requested');
    this.clearSession();
  }

  clearSession() {
    // Reset stato interno
    this.currentUser = null;
    this.profile = null;
    this.session = null;
    
    // Pulisci persistenza
    SessionPersistence.clearSession();
    
    console.log('[UserSession] Session cleared');
  }

  getCurrentUser() {
    return this.currentUser;
  }

  getAccessCode() {
    return this.currentUser?.access_key || null;
  }

  // Metodi stub per compatibilità
  async initializeSession() {
    return this.isAuthenticated();
  }

  async updateDisplayName(displayName) {
    if (this.currentUser) {
      this.currentUser.display_name = displayName;
      if (this.profile) {
        this.profile.display_name = displayName;
      }
      SessionPersistence.saveSession(this.currentUser);
    }
    return { success: true, user: this.currentUser };
  }

  async getUserStats() {
    return {
      success: true,
      stats: {
        totalPlayers: 0,
        totalFavorites: 0,
        totalTeams: 0,
        lastActivity: this.currentUser?.created_at || new Date().toISOString()
      }
    };
  }
}

// Export di compatibilità
export default SessionPersistence;
